import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import Highcharts from 'highcharts';
import { CacheService, ImageProviderService, ParameterHandlerService } from 'projects/common/src/app/services';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { groupBy, groupStatusByMachine, hexToRGB, msToHms } from 'projects/common/src/app/services/utils/utilities';
import { IShift } from 'src/app/modules/core/interfaces';
import { DatePresentationPipe } from 'src/app/modules/core/pipes/date-presentation.pipe';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { MachineContextService } from 'src/app/modules/core/services/parameter/machine-context/machine-context.service';
import { StateSidebarService } from 'src/app/modules/sidebar/services/state-sidebar.service';
import { IStatusGroup } from 'src/app/modules/widget/interfaces/status-group';
import { IStatusMachine } from 'src/app/modules/widget/interfaces/status-machine';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';
import { CommunicationService } from 'src/app/services/communication';
import { DashboardWidgetChartComponent } from '../../../dashboard-widget-chart/dashboard-widget-chart.component';
import { StatusModalComponent } from '../status-modal/status-modal.component';

@Component({
  selector: 'app-status-by-machine-group',
  templateUrl: './status-by-machine-group.component.html',
  styleUrls: ['./status-by-machine-group.component.scss'],
})
export class StatusByMachineGroupComponent extends DashboardWidgetChartComponent implements OnInit, OnDestroy {
  private positionY: number;
  private counter: number;
  public displayMessage = false;
  public showLegend: boolean;

  constructor(
    protected progressBarService: BooleanStateService,
    protected communication: CommunicationService,
    protected parameterService: ParameterHandlerService,
    protected sidebarService: StateSidebarService,
    protected cacheService: CacheService,
    protected machineContext: MachineContextService,
    private langService: LanguageService,
    private datePipe: DatePresentationPipe,
    private imageProvider: ImageProviderService,
    private modalService: NgbModal,
    protected screenViewService: ScreenViewService,
  ) {
    super(progressBarService, communication, parameterService, sidebarService, cacheService, machineContext, screenViewService);
    this.showLegend = false;
    this.initRequestParams();
  }

  /**
   * Init request params
   */
  protected initRequestParams(): void {
    this.params = {
      hasCompany: true,
      hasDate: true,
      hasShift: true,
      hasMachineType: true,
    };
  }

  /**
   * Functino to draw view
   *
   * @param chartOptions Options
   */
  public drawView(chartOptions: any): void {
    const machineType = this.parameterService.parameters.item('machine-context').value || 'no disponible';
    const date = this.parameterService.parameters.item('date').value;
    const shift = this.parameterService.parameters.item('shift').value as IShift;
    const user = this.parameterService.parameters.item('user-data').value;
    const { imageProvider } = this;
    const { modalService } = this;

    this.hasData = !chartOptions.length;

    chartOptions.machine = chartOptions;

    if (chartOptions.length) {
      this.hasData = true;
      this.positionY = 130;
      // Group by machine as array
      let groupByMachine = groupBy(chartOptions, (x: IStatusMachine) => x.machineName);
      groupByMachine = Object.keys(groupByMachine).map((key) => groupByMachine[key]);

      this.counter = 0;

      const arrayData = [];

      for (const machine of groupByMachine) {
        const statusData = groupStatusByMachine(machine);
        const serieData = this.createSerieData(statusData, groupByMachine.length);
        arrayData.push(serieData);
      }

      const series = [];
      for (const serie of arrayData) {
        for (const detail of serie) {
          series.push(detail);
        }
      }

      const data = {
        chart: {
          plotBackgroundColor: null,
          plotBorderWidth: null,
          plotShadow: false,
          type: 'pie',
          zoomType: 'xy',
          backgroundColor: '#F8F8F8',
        },
        credits: {
          enabled: false,
        },
        title: {
          text: '',
        },
        subtitle: {
          text: `${this.langService.message('company')}: ${user.company.name},
                  ${this.langService.message('device')}: ${machineType.name},
                  ${this.langService.message('date')}: ${this.datePipe.transform(date)},
                  ${this.langService.message('shift_')}: ${shift.name}`,
        },
        tooltip: {
          backgroundColor: '#433e3e',
          borderColor: '#433e3e',
          borderRadius: 2,
          style: {
            color: 'white',
          },
          useHTML: true,
          labelFormatter(): string {
            const image = imageProvider.getImage(`dashboard-${machineType.name}`, this.point.options.imageName);
            const color = this.point.options.color.replace('0.5', '1');
            const hoursTranslation = this.langService.message('hours');

            let html = `
                <!--<span>${this.point.options.machineName}</span> <br> <br>-->
                <div style="width: 210px; white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;">
                  <div style="float: left; width: 80px">
                    <div>
                      <img src="${image}" width="50">
                    </div>
                  </div>
  
                  <div>
                    <strong>${this.key}</strong> <br>
                    <div style="margin-left: 8px; color: ${color}">
                      <strong>${Number(this.percentage).toFixed(2)}%</strong>
                    </div>
                    <span>${msToHms(this.y)} de ${this.point.options.totalTime} ${hoursTranslation}</span>
                  </div>
                  <hr style="border-color: white">
                </div>
              `;

            for (const status of this.point.options.statusDetail as IStatusGroup[]) {
              const percentage = Number((Number(status.milliseconds) * 100) / Number(status.totalDifference)).toFixed(2);
              html += `
                  <div style="float: left;
                  width: 70%;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;">
                    <span style="color: ${color}">${status.name}</span>
                  </div>
                  <div style="float: right">
                    <span>${percentage}%</span>
                  </div>
                  <br>
                `;
            }
            return html;
          },
        },
        plotOptions: {
          pie: {
            shadow: false,
            cursor: 'pointer',
          },
          series: {
            states: {
              hover: {
                enabled: true,
                halo: {
                  size: 0,
                },
              },
            },
            point: {
              events: {
                click(): void {
                  const componentRef = modalService.open(StatusModalComponent, { centered: true, windowClass: 'status-machine-modal' });
                  componentRef.componentInstance.selectedStatus = this.options.statusDetail[0].parentStatusName;
                  componentRef.componentInstance.machineName = this.options.machineName;
                  componentRef.componentInstance.totalTime = this.options.totalTime;
                  componentRef.componentInstance.allStatus = this.options.allStatus;

                  componentRef.result.finally(() => {});
                },
              },
            },
          },
        },
        series,
      };

      this.chart = Highcharts.chart(this.chartTarget.nativeElement, data as any, (chart) => {
        if (this.positionY > 150) {
          chart.setSize(this.chartTarget.nativeElement.offsetWidth, this.positionY + 280);
        }

        this.showLegend = true;

        this.addLabelsToPie(chart, shift);
      });
    }
  }

  /**
   * Function to return the serie data for highchart
   *
   * @param statusData Data from status grouped
   */
  private createSerieData(statusData: IStatusGroup[], length: number): any {
    const serieData = [];
    const shift = this.parameterService.parameters.item('shift').value as IShift;

    if (shift.shiftId === -1) {
      const shifts = this.parameterService.parameters.item('shift-collection').value;
      shift.shiftSpan = 0;
      for (const current of shifts) {
        shift.shiftSpan += Number(current.shiftSpan);
      }
    }

    if (this.counter > 0 && this.counter % 4 === 0) {
      this.positionY += 300;
      this.counter = 0;
    }
    for (const status of statusData) {
      serieData.push({
        name: status.name,
        machineName: status.machineName,
        imageName: status.imageName,
        parentStatus: status.parentStatusName,
        y: status.milliseconds,
        color: hexToRGB(status.color, '0.5'),
        borderColor: status.color,
        borderWidth: 1,
        totalDifference: status.totalDifference,
        statusDetail: status.statusInside,
        allStatus: statusData.filter((value) => value.machineName === status.machineName).map((value) => value.statusInside),
        totalTime: shift.shiftSpan,
      });
    }

    const currentWidth = this.chartTarget.nativeElement.offsetWidth;
    let positionX = 0;
    length = length > 4 ? 4 : length;
    if (currentWidth < 1200) {
      // currentWidth = 1200;
      positionX = (currentWidth / (length || 1)) * this.counter + 95;
    } else {
      positionX = (currentWidth / (length || 1)) * this.counter + 150;
    }

    const data = [
      {
        type: 'pie',
        data: serieData,
        // size: '55%',
        size: 230,
        innerSize: '40%',
        dataLabels: {
          enabled: true,
          formatter(): string {
            if (this.percentage > 10) {
              return `${Number(this.percentage).toFixed(0)}%`;
            }
            return '';
          },
          distance: -50,
          style: {
            color: 'white',
            borderWidth: 0,
            borderColor: 'white',
          },
        },
        center: [positionX, this.positionY],
      },
    ];

    this.counter++;
    return data;
  }

  /**
   * Function to add the text and machine names
   *
   * @param chart Current chart
   */
  private addLabelsToPie(chart: Highcharts.Chart, shift?: IShift): void {
    const machineType = this.parameterService.parameters.item('machine-context').value || 'no disponible';
    const { imageProvider } = this;
    const { series } = chart;

    if (shift.shiftId === -1) {
      const shifts = this.parameterService.parameters.item('shift-collection').value;
      shift.shiftSpan = 0;
      for (const current of shifts) {
        shift.shiftSpan += Number(current.shiftSpan);
      }
    }

    for (const option of series as any) {
      const serie = option.userOptions;
      const machineName = `<span style="font-weight: 400; color: #433e3e; cursor: pointer;  ">${serie.data[0].machineName}</span>`;
      const positionX = Number(option.options.center[0]) - (Number(String(serie.data[0].machineName).length) < 7 ? -12 : 20);
      const positionY = option.options.center[1];
      const hoursTranslation = this.langService.message('hours');
      const showingTranslation = this.langService.message('showing');

      const text = `<span style="color: #707070; font-size: 12px;">${showingTranslation}</span> <br>
                    <span style="color: #707070; font-size: 12px;">${msToHms(serie.data[0].totalDifference)}</span> <br>
                    <span style="color: #707070; font-size: 12px;">de ${shift.shiftSpan} hrs</span>`;

      chart.renderer.text(text, option.options.center[0], option.options.center[1] + 50).add();
      chart.renderer
        .text(machineName, positionX, positionY + 200)
        .add()
        .on('mouseover', () => {
          chart.tooltip.update({
            positioner(): any {
              return { x: positionX - 80, y: positionY };
            },
            formatter(): string {
              let html = `
              <div style="width: 190px; white-space: nowrap;
              overflow: hidden;
              text-align: center;
              text-overflow: ellipsis;">
                <span>${(this.point.options as any).machineName}</span>

                <div>
                  <span>${msToHms(serie.data[0].totalDifference)} de ${(this.point.options as any).totalTime} ${hoursTranslation}</span>
                </div>
                <hr style="border-color: white">
              </div>
            `;
              for (const group of (this.point.options as any).allStatus as IStatusGroup[][]) {
                for (const status of group) {
                  const percentage = Number((Number(status.milliseconds) * 100) / Number(status.totalDifference)).toFixed(2);
                  const statusColor = status.color.replace('0.5', '1');
                  html += `
                  <div style="float: left;
                  width: 70%;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;">
                    <span style="color: ${statusColor}">${status.name}</span>
                  </div>
                  <div style="float: right">
                    <span>${percentage}%</span>
                  </div>
                  <br>
                `;
                }
              }
              return html;
            },
          });

          chart.tooltip.refresh(option.points[0]);
        })
        .on('mouseleave', () => {
          chart.tooltip.update({
            positioner(width, height, point): { x: number; y: number } {
              return { x: point.plotX + 50, y: point.plotY };
            },
            formatter(): string {
              const image = imageProvider.getImage(`dashboard-${machineType.name}`, (this.point.options as any).imageName);
              const color = (this.point.options as any).color.replace('0.5', '1');
              const hours = Number(Number(this.y) / 1000 / 60 / 60).toFixed(2);

              let html = `
              <!--<span>${(this.point.options as any).machineName}</span> <br> <br>-->
              <div style="width: 190px; white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;">
                <div style="float: left; width: 80px">
                  <div>
                    <img src="${image}" width="50">
                  </div>
                </div>

                <div>
                  <strong>${this.key}</strong> <br>
                  <div style="margin-left: 8px; color: ${color}">
                    <strong>${Number(this.percentage).toFixed(2)}%</strong>
                  </div>
                  <span>${hours} de ${(this.point.options as any).totalTime} ${hoursTranslation}</span>
                </div>
                <hr style="border-color: white">
              </div>
            `;

              for (const status of (this.point.options as any).statusDetail as IStatusGroup[]) {
                const percentage = Number((Number(status.milliseconds) * 100) / Number(status.totalDifference)).toFixed(2);
                html += `
                <div style="float: left;
                width: 70%;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;">
                  <span style="color: ${color}">${status.name}</span>
                </div>
                <div style="float: right">
                  <span>${percentage}%</span>
                </div>
                <br>
              `;
              }
              return html;
            },
          });
        });
    }
  }

  /**
   * On destroy component
   */
  public onDestroy(): void {
    super.ngOnDestroy();
  }
}
